import type {
  DynamicRegenSync,
  FlowInstancePortfoliosResponse,
  UpdateGoalPortfolioRequest,
} from "@repo/shared/types";
import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import * as portfoliosApi from "@/lib/api/flow-instances/portfolios";
import { useInvalidateFlowCompletion } from "./use-flow-completion";
import { goalSnapshotQueryKeys, useInvalidateAssetClassForecast } from "./use-goal-snapshots";
import { portfolioSnapshotQueryKeys } from "./use-portfolio-snapshots";

// --- Query Keys ---

const keys = {
  portfolios: (goalId: string | null) => ["flowInstances", "goals", goalId, "portfolios"],
  // QL-676 — every portfolio of the instance, grouped by goal (the Portfolio
  // List). Nested under the instance's goal-list key on purpose, so every
  // goal-list invalidation (goal added, deleted, re-scored) refreshes it too.
  // Portfolio mutations reach it through `invalidateGoalPortfolios`.
  instancePortfolios: (instanceId: string | null) =>
    ["flowInstances", instanceId, "goals", "allPortfolios"] as const,
  portfolio: (portfolioId: string | null) => ["flowInstances", "portfolios", portfolioId],
  // QL-72 — nested under the portfolio so its family invalidation covers it.
  // The goal's score and platform are part of the key: they decide which
  // candidate is the matching one, and a re-score or platform change must
  // refetch the list.
  modelCandidates: (
    portfolioId: string | null,
    goalRiskScore: number | null,
    platformId: string | null,
  ) => ["flowInstances", "portfolios", portfolioId, "modelCandidates", goalRiskScore, platformId],
  // QL-362 — prefix of every portfolio detail. Account-minimum violations are a
  // goal-level aggregate served on each contributing portfolio, so a composition
  // change in one portfolio can flip its siblings' payloads.
  allPortfolioDetails: () => ["flowInstances", "portfolios"],
};

// --- Invalidation ---

/**
 * QL-676 — invalidate the instance-wide portfolio read of every instance.
 * Matches the `instancePortfolios` shape for any instance id, so a mutation
 * that only knows a goal or portfolio id still reaches it. Only one instance
 * is open at a time, so this refetches one query.
 */
export function invalidateInstancePortfolios(queryClient: QueryClient) {
  return queryClient.invalidateQueries({
    predicate: ({ queryKey: k }) =>
      k.length === 4 && k[0] === "flowInstances" && k[2] === "goals" && k[3] === "allPortfolios",
  });
}

/**
 * QL-676 — a goal's portfolio list changed: invalidate its per-goal list AND
 * the instance-wide read the Portfolio List renders from. Use this instead of
 * invalidating `keys.portfolios(goalId)` alone.
 */
export function invalidateGoalPortfolios(queryClient: QueryClient, goalId: string) {
  queryClient.invalidateQueries({ queryKey: keys.portfolios(goalId) });
  return invalidateInstancePortfolios(queryClient);
}

/**
 * QL-774 — a portfolio was added or removed: refresh the instance's goal list
 * too. Its slim `portfolios` (follow-up lineage, type, schedule) feed the
 * conditional rules, and a global rule like `__followup_mode__` can flip whole
 * pages when a follow-up portfolio comes or goes. With an `instanceId` it
 * targets that instance's `["flowInstances", instanceId, "goals"]` list.
 * Without one (a failed create, a delete called without it) it matches that
 * 3-segment list for every instance. The per-goal
 * `["flowInstances", "goals", goalId]` detail is left alone either way.
 */
export function invalidateInstanceGoalList(queryClient: QueryClient, instanceId?: string) {
  if (instanceId) {
    return queryClient.invalidateQueries({
      queryKey: ["flowInstances", instanceId, "goals"],
      exact: true,
    });
  }
  return queryClient.invalidateQueries({
    predicate: ({ queryKey: k }) =>
      k.length === 3 && k[0] === "flowInstances" && k[1] !== "goals" && k[2] === "goals",
  });
}

/**
 * Seed each goal's per-goal list from an instance-wide read, so per-goal
 * readers (the goal context, the portfolio pages) share what it fetched. A
 * goal is seeded only when its own entry is older than this fetch, is not
 * fetching, and was not invalidated — so a response that was in flight never
 * overwrites fresher per-goal data or hides a pending refetch.
 */
export function seedGoalPortfolios(
  queryClient: QueryClient,
  goalKey: (goalId: string) => readonly unknown[],
  response: FlowInstancePortfoliosResponse,
  fetchStartedAt: number,
) {
  for (const { goalId, portfolios } of response.goals) {
    const state = queryClient.getQueryState(goalKey(goalId));
    if (state?.fetchStatus === "fetching" || state?.isInvalidated) continue;
    if (state?.data !== undefined && state.dataUpdatedAt >= fetchStartedAt) continue;
    queryClient.setQueryData(goalKey(goalId), portfolios, { updatedAt: fetchStartedAt });
  }
}

/**
 * QL-362 — account-minimum violations aggregate across the goal's
 * portfolios (shared accounts), so a composition or schedule change in one
 * portfolio leaves its siblings' payloads stale too. Siblings only need their
 * detail refetched, so the predicate stops at the 3-segment detail key — the
 * bare prefix would also match every portfolio's snapshot queries and
 * refetch all analytics on each edit. The changed portfolio gets
 * its whole sub-family (detail + everything nested under it).
 */
export function invalidatePortfolioFamily(queryClient: QueryClient, changedPortfolioId?: string) {
  if (changedPortfolioId) {
    queryClient.invalidateQueries({ queryKey: keys.portfolio(changedPortfolioId) });
  }
  return queryClient.invalidateQueries({
    queryKey: keys.allPortfolioDetails(),
    predicate: (query) => query.queryKey.length === 3,
  });
}

// --- Query Hooks ---

export function useFlowInstancePortfolios(goalId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.portfolios(goalId),
    queryFn: () => portfoliosApi.getPortfolios(goalId!),
    enabled: !!goalId,
  });
  const queryClient = useQueryClient();
  const mutate = () => (goalId ? invalidateGoalPortfolios(queryClient, goalId) : undefined);
  return { portfolios: data ?? [], error, isLoading, mutate };
}

/**
 * Portfolios for several goals at once — one entry per goal id, in order.
 * QL-676 — one instance-wide request whatever the goal count, instead of one
 * per goal; the response seeds each goal's `keys.portfolios(goalId)` entry so
 * per-goal readers share it. A goal the response does not know yet (added
 * since it was fetched) reads as empty until the refetch its invalidation
 * triggers lands. With exactly one goal it reads that goal's own list instead:
 * a single-goal flow mounts the goal context (which reads it) on every page,
 * so the two share one request. No goals yet: no request.
 */
export function useFlowInstancePortfoliosForGoals(instanceId: string | null, goalIds: string[]) {
  const queryClient = useQueryClient();
  const singleGoalId = goalIds.length === 1 ? goalIds[0]! : null;
  const single = useFlowInstancePortfolios(singleGoalId);
  const { data, isLoading } = useQuery({
    queryKey: keys.instancePortfolios(instanceId),
    // Deliberately does not read the AbortSignal: reading it makes React Query
    // abort the fetch whenever the last observer unmounts. A superseded response
    // is harmless — `seedGoalPortfolios` skips every goal that was invalidated,
    // is refetching, or holds newer data.
    queryFn: async () => {
      const startedAt = Date.now();
      const response = await portfoliosApi.getInstancePortfolios(instanceId!);
      seedGoalPortfolios(queryClient, keys.portfolios, response, startedAt);
      return response;
    },
    enabled: !!instanceId && goalIds.length > 1,
  });
  return useMemo(() => {
    if (singleGoalId) return [{ portfolios: single.portfolios, isLoading: single.isLoading }];
    const byGoal = new Map(data?.goals.map((g) => [g.goalId, g.portfolios]));
    return goalIds.map((goalId) => ({ portfolios: byGoal.get(goalId) ?? [], isLoading }));
  }, [data, goalIds, isLoading, singleGoalId, single.portfolios, single.isLoading]);
}

export function usePortfolio(portfolioId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.portfolio(portfolioId),
    queryFn: () => portfoliosApi.getPortfolio(portfolioId!),
    enabled: !!portfolioId,
  });
  const queryClient = useQueryClient();
  const mutate = () => queryClient.invalidateQueries({ queryKey: keys.portfolio(portfolioId) });
  return { portfolio: data ?? null, error, isLoading, mutate };
}

/** QL-72 — model-portfolio override candidates; enable only when the picker shows. */
export function useModelPortfolioCandidates(
  portfolioId: string | null,
  options: { enabled: boolean; goalRiskScore: number | null; platformId: string | null },
) {
  return useQuery({
    queryKey: keys.modelCandidates(portfolioId, options.goalRiskScore, options.platformId),
    queryFn: () => portfoliosApi.getModelPortfolioCandidates(portfolioId!),
    enabled: !!portfolioId && options.enabled,
  });
}

// --- Mutation Hooks ---

export function useCreatePortfolio(goalId: string) {
  const queryClient = useQueryClient();
  const invalidateCompletion = useInvalidateFlowCompletion();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => portfoliosApi.createPortfolio(goalId, data),
    onSettled: (data: { instanceId?: string } | undefined) => {
      invalidateGoalPortfolios(queryClient, goalId);
      invalidateInstanceGoalList(queryClient, data?.instanceId);
    },
    onSuccess: (data: { instanceId?: string }) => {
      if (data?.instanceId) invalidateCompletion(data.instanceId);
    },
  });
}

export function useUpdatePortfolio() {
  const queryClient = useQueryClient();
  const invalidateCompletion = useInvalidateFlowCompletion();
  return useMutation({
    mutationFn: ({
      portfolioId,
      data,
    }: {
      portfolioId: string;
      data: UpdateGoalPortfolioRequest;
    }) => portfoliosApi.updatePortfolio(portfolioId, data),
    onSettled: (_data, _error, vars) => {
      invalidatePortfolioFamily(queryClient, vars.portfolioId);
    },
    onSuccess: (data: { instanceId?: string; goalId?: string }) => {
      if (data?.goalId) {
        invalidateGoalPortfolios(queryClient, data.goalId);
      }
      if (data?.instanceId) invalidateCompletion(data.instanceId);
    },
  });
}

export function useDeletePortfolio() {
  const queryClient = useQueryClient();
  const invalidateCompletion = useInvalidateFlowCompletion();
  const invalidateAssetClassForecast = useInvalidateAssetClassForecast();
  return useMutation({
    mutationFn: ({ portfolioId }: { portfolioId: string; goalId?: string; instanceId?: string }) =>
      portfoliosApi.deletePortfolio(portfolioId),
    onSettled: (_data, _error, vars) => {
      invalidatePortfolioFamily(queryClient, vars.portfolioId);
      invalidateInstanceGoalList(queryClient, vars.instanceId);
      if (vars.goalId) {
        invalidateGoalPortfolios(queryClient, vars.goalId);
        // The goal's mandate check no longer reads this portfolio (PR 598 review).
        queryClient.invalidateQueries({ queryKey: goalSnapshotQueryKeys.mandate(vars.goalId) });
      }
    },
    onSuccess: (_data, vars) => {
      if (vars.instanceId) invalidateCompletion(vars.instanceId);
      // QL-315 — the server regenerates both scopes of the merge after a
      // portfolio delete (the goal's row and the instance's), and this path
      // carries no refreshHints to route that through.
      invalidateAssetClassForecast();
    },
  });
}

export function useAddPortfolioProduct() {
  const queryClient = useQueryClient();
  const invalidateCompletion = useInvalidateFlowCompletion();
  return useMutation({
    mutationFn: ({
      portfolioId,
      data,
    }: {
      portfolioId: string;
      goalId?: string;
      instanceId?: string;
      data: { productId: string; amount?: number; units?: number; weight: number };
    }) => portfoliosApi.addPortfolioProduct(portfolioId, data),
    onSettled: (_data, _error, vars) => {
      invalidatePortfolioFamily(queryClient, vars.portfolioId);
      if (vars.goalId) {
        invalidateGoalPortfolios(queryClient, vars.goalId);
      }
    },
    onSuccess: (_data, vars) => {
      if (vars.instanceId) invalidateCompletion(vars.instanceId);
    },
  });
}

export function useUpdatePortfolioProduct() {
  const queryClient = useQueryClient();
  const invalidateCompletion = useInvalidateFlowCompletion();
  return useMutation({
    mutationFn: ({
      productId,
      data,
    }: {
      productId: string;
      portfolioId?: string;
      goalId?: string;
      instanceId?: string;
      data: { amount?: number; units?: number; weight?: number; order?: number };
    }) => portfoliosApi.updatePortfolioProduct(productId, data),
    onSettled: (_data, _error, vars) => {
      invalidatePortfolioFamily(queryClient, vars.portfolioId);
      if (vars.goalId) {
        invalidateGoalPortfolios(queryClient, vars.goalId);
      }
    },
    onSuccess: (_data, vars) => {
      if (vars.instanceId) invalidateCompletion(vars.instanceId);
    },
  });
}

export function useDeletePortfolioProduct() {
  const queryClient = useQueryClient();
  const invalidateCompletion = useInvalidateFlowCompletion();
  return useMutation({
    mutationFn: ({
      productId,
    }: {
      productId: string;
      portfolioId?: string;
      goalId?: string;
      instanceId?: string;
    }) => portfoliosApi.deletePortfolioProduct(productId),
    onSettled: (data, _error, vars) => {
      invalidatePortfolioFamily(queryClient, data?.portfolioId ?? vars.portfolioId);
      if (vars.goalId) {
        invalidateGoalPortfolios(queryClient, vars.goalId);
      }
    },
    onSuccess: (_data, vars) => {
      if (vars.instanceId) invalidateCompletion(vars.instanceId);
    },
  });
}

// --- Dynamic regeneration invalidation (QP-185) ---

/**
 * Invalidate the portfolio queries touched by a server-side dynamic-portfolio
 * regeneration (`dynamicSync` on save/submit responses) so the read-only
 * composition refreshes — and the snapshot queries too, since regeneration now
 * also rebuilds the portfolio's analytics snapshots (QP-185).
 */
export function useInvalidateDynamicSync() {
  const queryClient = useQueryClient();
  return (sync?: DynamicRegenSync) => {
    if (!sync?.changed) return;
    for (const portfolioId of sync.portfolioIds) {
      queryClient.invalidateQueries({ queryKey: keys.portfolio(portfolioId) });
      queryClient.invalidateQueries({
        queryKey: portfolioSnapshotQueryKeys.allForPortfolio(portfolioId),
      });
    }
    for (const goalId of sync.goalIds) {
      invalidateGoalPortfolios(queryClient, goalId);
    }
  };
}

// --- Exported Query Keys (for targeted invalidation in widget mutations) ---

export { keys as portfolioQueryKeys };
